
import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Droplets, Activity, Settings, Info, History, Calendar, Ruler } from 'lucide-react';
import { WaterLog, UserSettings, ActivityLog, ScaleLog } from '../types';
import { t } from '../locales';
import { simulateDayHydration, calculateWatsonTBW } from '../services/hydrationService';

interface WaterModalProps {
  isOpen: boolean;
  onClose: () => void;
  logs: WaterLog[];
  activityLogs: ActivityLog[];
  scaleLogs: ScaleLog[]; // Added scaleLogs prop
  onAddWater: (amount: number) => void;
  onDeleteLog: (id: string) => void;
  settings: UserSettings;
  onUpdateSettings: (newSettings: UserSettings) => void;
}

const WaterModal: React.FC<WaterModalProps> = ({ 
    isOpen, onClose, logs, activityLogs, scaleLogs, onAddWater, onDeleteLog, settings, onUpdateSettings 
}) => {
  const [activeTab, setActiveTab] = useState<'track' | 'settings'>('track');
  
  if (!isOpen) return null;

  const todayDate = new Date();
  const todaysLogs = logs.filter(l => new Date(l.timestamp).toDateString() === todayDate.toDateString());
  const totalVolume = todaysLogs.reduce((s, l) => s + (l.amount || 0), 0);
  
  const intakeGoal = settings.waterGoal || 2000;
  const goalPercentage = Math.min(100, Math.round((totalVolume / intakeGoal) * 100));

  const hydrationCurve = useMemo(() => {
      return simulateDayHydration(
          settings.weight || 70, 
          logs, 
          activityLogs, 
          todayDate,
          { gender: settings.gender, height: settings.height, age: settings.age },
          scaleLogs // Pass scaleLogs to simulation
      );
  }, [logs, activityLogs, scaleLogs, settings.weight, settings.gender, settings.height, settings.age, todayDate]);

  const currentPhysiologicalLevel = hydrationCurve.find(p => p.hour >= todayDate.getHours())?.level || hydrationCurve[hydrationCurve.length-1].level;
  const physiologicalPct = (currentPhysiologicalLevel * 100).toFixed(1);

  const getStatusInfo = (level: number) => {
      if (level > 0.70) return { label: 'Optimal High', color: 'text-emerald-600' };
      if (level >= 0.55) return { label: 'Optimal', color: 'text-emerald-500' };
      if (level > 0.50) return { label: 'Mildly Dehydrated', color: 'text-amber-500' };
      return { label: 'Dehydrated', color: 'text-red-500' };
  };

  const status = getStatusInfo(currentPhysiologicalLevel);
  const lastLog = todaysLogs.length > 0 ? [...todaysLogs].sort((a,b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0] : null;

  // --- SVG Plotting Logic ---
  const CHART_H = 160;
  const CHART_W = 320;
  const MIN_DISPLAY_LEVEL = 0.40; 
  const MAX_DISPLAY_LEVEL = 0.85;

  const scaleY = (level: number) => {
      const range = MAX_DISPLAY_LEVEL - MIN_DISPLAY_LEVEL;
      const normalized = (level - MIN_DISPLAY_LEVEL) / range;
      return CHART_H - (normalized * CHART_H);
  };

  const curvePath = useMemo(() => {
      const points = hydrationCurve.map(p => {
          const x = (p.hour / 24) * CHART_W;
          const y = scaleY(Math.max(MIN_DISPLAY_LEVEL, Math.min(MAX_DISPLAY_LEVEL, p.level)));
          return `${x},${y}`;
      });
      return `M ${points.join(' L ')}`;
  }, [hydrationCurve]);

  const handleGoalUpdate = (val: string) => {
      const num = parseInt(val, 10);
      onUpdateSettings({ ...settings, waterGoal: isNaN(num) ? 2000 : num });
  };

  const handlePresetUpdate = (val: string) => {
      const num = parseInt(val, 10);
      onUpdateSettings({ ...settings, standardWaterVolume: isNaN(num) ? 130 : num });
  };

  // Helper to get hydration % at a specific log time (only for measures)
  const getLogPercentage = (log: WaterLog) => {
      if (log.measuredPercentage !== undefined) return log.measuredPercentage.toFixed(1);
      const logTime = new Date(log.timestamp);
      const logHour = logTime.getHours() + logTime.getMinutes() / 60;
      const point = hydrationCurve.reduce((prev, curr) => 
          Math.abs(curr.hour - logHour) < Math.abs(prev.hour - logHour) ? curr : prev
      );
      return (point.level * 100).toFixed(1);
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-50 dark:bg-gray-900 animate-slideIn">
      <div className="w-full h-full max-w-md mx-auto flex flex-col bg-white dark:bg-gray-900 shadow-xl overflow-hidden">
        
        {/* Header */}
        <div className="px-4 py-4 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-cyan-50/50 dark:bg-gray-900/80 backdrop-blur-md sticky top-0 z-10 pt-safe">
            <h2 className="text-xl font-bold text-cyan-700 dark:text-cyan-400 flex items-center gap-2">
                <Droplets className="h-6 w-6" />
                {t(settings.language, 'hydration')}
            </h2>
            <button onClick={onClose} className="p-2 rounded-full bg-white dark:bg-gray-800 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 shadow-sm border border-gray-100 dark:border-gray-700">
                <X size={24} />
            </button>
        </div>

        {/* Tabs */}
        <div className="flex p-4 gap-4 bg-cyan-50/30 dark:bg-gray-900/50">
            <button onClick={() => setActiveTab('track')} className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${activeTab === 'track' ? 'bg-cyan-500 text-white shadow-md' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-100 dark:border-gray-700'}`}>
                <Droplets size={16} />
                Tracking
            </button>
            <button onClick={() => setActiveTab('settings')} className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${activeTab === 'settings' ? 'bg-cyan-500 text-white shadow-md' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-100 dark:border-gray-700'}`}>
                <Settings size={16} />
                {t(settings.language, 'settings')}
            </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 pb-20 min-h-0 bg-cyan-50/10 dark:bg-gray-900">
            {activeTab === 'track' && (
                <div className="flex flex-col gap-6 pt-4 animate-slideIn">
                    {/* Goal Progress Section */}
                    <div className="flex items-center justify-between gap-4 bg-white dark:bg-gray-800 p-4 rounded-2xl border border-cyan-100/50 dark:border-gray-700 shadow-sm">
                        <div className="relative w-44 h-44 flex-shrink-0">
                            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                                <circle cx="50" cy="50" r="42" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-gray-200 dark:text-gray-800" />
                                <circle 
                                    cx="50" cy="50" r="42" 
                                    stroke="currentColor" 
                                    strokeWidth="8" 
                                    fill="transparent" 
                                    strokeDasharray={263}
                                    strokeDashoffset={263 - (263 * goalPercentage) / 100} 
                                    className="text-cyan-500 transition-all duration-1000 ease-out" 
                                    strokeLinecap="round" 
                                />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="text-3xl font-black text-cyan-600 dark:text-cyan-400">{totalVolume}<span className="text-sm ml-0.5">ml</span></span>
                                <div className="text-[9px] text-gray-400 uppercase tracking-widest font-bold mt-1 border-t border-gray-50 dark:border-gray-700 pt-1">Goal: {intakeGoal}ml</div>
                                {lastLog && (
                                    <button onClick={() => onDeleteLog(lastLog.id)} className="absolute bottom-1 w-7 h-7 flex items-center justify-center bg-red-50 dark:bg-red-900/20 text-red-500 rounded-full hover:bg-red-100 shadow-sm transition-transform active:scale-90 border border-red-100 dark:border-red-900/30">
                                        <X size={12} />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col gap-2 flex-1">
                            <div className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1 text-center">Presets</div>
                            <div className="flex flex-col gap-2">
                                {[130, 250, 400].map(vol => (
                                    <button 
                                        key={vol} 
                                        onClick={() => onAddWater(vol)} 
                                        className={`py-3 rounded-xl flex flex-col items-center justify-center active:scale-95 transition-all shadow-sm border ${settings.standardWaterVolume === vol ? 'bg-cyan-500 text-white border-cyan-600' : 'bg-white dark:bg-gray-700 border-gray-100 dark:border-gray-600 hover:border-cyan-400'}`}
                                    >
                                        <span className={`font-bold ${settings.standardWaterVolume === vol ? 'text-white' : 'text-gray-700 dark:text-gray-200'} text-base`}>+{vol}<span className="text-[10px] ml-0.5">ml</span></span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Metabolic Chart Section */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                                    <Activity size={12} className="text-emerald-500" />
                                    Biological Hydration
                                </h3>
                                <div className={`text-sm font-bold ${status.color} mt-0.5`}>{status.label} ({physiologicalPct}%)</div>
                            </div>
                            <div className="bg-cyan-50 dark:bg-cyan-900/20 px-2 py-1 rounded text-[9px] font-bold text-cyan-600 dark:text-cyan-400">TARGET: 55-70%</div>
                        </div>

                        <div className="relative w-full">
                            <div className="h-40 w-full border-b border-gray-200 dark:border-gray-600 relative overflow-hidden bg-gray-50/30 dark:bg-gray-900/30 rounded-lg">
                                <svg className="w-full h-full overflow-visible">
                                    {/* Optimal Zone Highlight (55% to 70%) */}
                                    <rect x="0" y={scaleY(0.70)} width="100%" height={scaleY(0.55) - scaleY(0.70)} fill="rgba(16, 185, 129, 0.08)" />
                                    
                                    {/* Dehydration Warning Zone (Below 55%) */}
                                    <rect x="0" y={scaleY(0.55)} width="100%" height={CHART_H - scaleY(0.55)} fill="rgba(239, 68, 68, 0.06)" />
                                    
                                    {/* Grid Lines at 55% and 70% */}
                                    {[0.55, 0.70].map(val => (
                                        <line key={val} x1="0" y1={scaleY(val)} x2="100%" y2={scaleY(val)} stroke="currentColor" className="text-gray-200 dark:text-gray-700" strokeWidth="1" strokeDasharray="4 2" />
                                    ))}

                                    {/* The Simulation Curve */}
                                    <path 
                                        d={curvePath}
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                        className="text-cyan-500 vector-effect-non-scaling-stroke"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    
                                    {/* Current Time Marker */}
                                    <line 
                                        x1={`${(todayDate.getHours() / 24) * 100}%`} 
                                        y1="0" 
                                        x2={`${(todayDate.getHours() / 24) * 100}%`} 
                                        y2="100%" 
                                        stroke="#ef4444" 
                                        strokeWidth="1.5" 
                                        strokeDasharray="3 2" 
                                    />
                                </svg>
                                
                                {/* Optimal Zone Label */}
                                <div className="absolute top-[45px] left-2 text-[8px] font-black text-emerald-600/40 uppercase tracking-widest">Ideal (55-70%)</div>
                            </div>
                            <div className="flex justify-between text-[9px] text-gray-400 font-mono pt-2 px-1">
                                <span>00:00</span>
                                <span>12:00</span>
                                <span>24:00</span>
                            </div>
                        </div>
                    </div>

                    {/* Hydration Record History Container */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-cyan-100/50 dark:border-gray-700 overflow-hidden">
                        <div className="px-4 py-3 bg-cyan-50/50 dark:bg-gray-800/50 border-b border-cyan-100/50 dark:border-gray-700 flex items-center justify-between">
                            <h3 className="text-xs font-bold text-cyan-700 dark:text-cyan-400 uppercase tracking-wider flex items-center gap-2">
                                <History size={14} />
                                Hydration Records
                            </h3>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Read Only</span>
                        </div>
                        <div className="max-h-60 overflow-y-auto">
                            {todaysLogs.length === 0 ? (
                                <div className="py-8 text-center text-gray-400 text-xs italic">No logs recorded for today</div>
                            ) : (
                                <table className="w-full text-left border-collapse">
                                    <thead className="bg-gray-50/50 dark:bg-gray-900/50 sticky top-0 z-10">
                                        <tr className="border-b border-gray-100 dark:border-gray-800">
                                            <th className="px-4 py-2 text-[9px] font-black text-gray-400 uppercase tracking-widest">Time</th>
                                            <th className="px-4 py-2 text-[9px] font-black text-gray-400 uppercase tracking-widest">Date</th>
                                            <th className="px-4 py-2 text-[9px] font-black text-gray-400 uppercase tracking-widest text-right">Biological %</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                                        {[...todaysLogs].sort((a,b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).map((log) => {
                                            const logTime = new Date(log.timestamp);
                                            const timeStr = logTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                            const dateStr = logTime.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
                                            const percentage = getLogPercentage(log);
                                            const isMeasure = log.measuredPercentage !== undefined;

                                            return (
                                                <tr key={log.id} className="hover:bg-cyan-50/20 dark:hover:bg-cyan-900/5 transition-colors">
                                                    <td className="px-4 py-3 text-xs font-bold text-gray-700 dark:text-gray-300">
                                                        <div className="flex items-center gap-1.5">
                                                            {isMeasure && <Ruler size={10} className="text-cyan-500" />}
                                                            {timeStr}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-[10px] font-medium text-gray-400 dark:text-gray-500">{dateStr}</td>
                                                    <td className="px-4 py-3 text-xs font-black text-cyan-600 dark:text-cyan-400 text-right">
                                                        {percentage}%
                                                        <span className={`text-[8px] font-bold ml-1 ${isMeasure ? 'text-cyan-400 uppercase' : 'text-gray-300'}`}>
                                                            ({isMeasure ? 'MEASURE' : `${log.amount}ml`})
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'settings' && (
                <div className="flex flex-col gap-5 pt-6 animate-slideIn">
                    {/* Goal & Preset Settings */}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 space-y-5">
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                             <Settings size={14} /> Intake Configuration
                        </h4>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-2">Daily Water/Liquid Goal (ml)</label>
                                <div className="flex items-center gap-3">
                                    <input 
                                        type="range" min="1000" max="4000" step="50"
                                        value={settings.waterGoal || 2000}
                                        onChange={(e) => handleGoalUpdate(e.target.value)}
                                        className="flex-1 accent-cyan-500 h-2 bg-gray-100 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                                    />
                                    <input 
                                        type="number" 
                                        value={settings.waterGoal || 2000}
                                        onChange={(e) => handleGoalUpdate(e.target.value)}
                                        className="w-20 p-2 text-center bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg text-sm font-bold text-cyan-600"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-2">Double-Tap Default (ml)</label>
                                <div className="flex flex-col gap-3">
                                    <div className="flex gap-2">
                                        {[130, 250, 400].map(val => (
                                            <button 
                                                key={val}
                                                onClick={() => handlePresetUpdate(String(val))}
                                                className={`flex-1 py-3 rounded-lg text-xs font-bold transition-all border ${settings.standardWaterVolume === val ? 'bg-cyan-500 text-white border-cyan-600 shadow-sm' : 'bg-gray-50 dark:bg-gray-700 text-gray-500 border-gray-200 dark:border-gray-600 hover:border-cyan-400'}`}
                                            >
                                                {val}ml
                                            </button>
                                        ))}
                                    </div>
                                    <div className="flex items-center gap-3 bg-gray-50 dark:bg-gray-900/50 p-2 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase ml-1">Custom:</span>
                                        <input 
                                            type="number" 
                                            value={settings.standardWaterVolume}
                                            onChange={(e) => handlePresetUpdate(e.target.value)}
                                            className="flex-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm font-bold text-cyan-600 outline-none focus:ring-1 focus:ring-cyan-500"
                                            placeholder="Enter ml..."
                                        />
                                    </div>
                                </div>
                                <p className="text-[9px] text-gray-400 mt-2 italic">Sets the volume added when you double-tap the water drop button on the home screen.</p>
                            </div>
                        </div>
                    </div>

                    {/* Biological Stats Section */}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 space-y-4">
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                            <Info size={14} /> Biological Range
                        </h4>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                                <span className="block text-[10px] font-bold text-gray-400 uppercase">Current Level</span>
                                <span className="text-lg font-black text-cyan-600">{physiologicalPct}%</span>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                                <span className="block text-[10px] font-bold text-gray-400 uppercase">Target Range</span>
                                <span className="text-lg font-black text-emerald-600">55 - 70%</span>
                            </div>
                        </div>

                        <div className="space-y-2 border-t border-gray-50 dark:border-gray-700 pt-4">
                            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                                <span>Age</span>
                                <span className="font-bold">{settings.age} years</span>
                            </div>
                            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                                <span>Gender Profile</span>
                                <span className="font-bold capitalize">{settings.gender}</span>
                            </div>
                        </div>

                        <p className="text-[10px] text-gray-400 leading-relaxed bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg border border-dashed border-gray-200 dark:border-gray-700">
                            The hydration scale is adjusted to your body's specific biological range. Ideally, you should maintain between 55% and 70% of your theoretical baseline.
                        </p>
                    </div>
                </div>
            )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WaterModal;